import { createActionGroup, createFeature, createReducer, createSelector, on, props } from '@ngrx/store';

import { ActionSource } from '../../state/action-sources';

export interface DocumentSlot {
  docType: string;
  name: string;
  engine?: string;
  templateUrl?: string;
}

const STANDARD_DOCUMENTS_PREFIX = 'standard-documents.';
export const DEFAULT_ESTIMATE_KEY = 'document-defaults.estimate';
export const DOCUMENT_ORDER_KEY = 'document-defaults.order';
const DEFAULT_ESTIMATE_DOC_TYPE = 'estimate';

export function parseDefaultEstimate(configs: { key: string; value?: string | null }[]): string {
  const value = configs.find((config) => config.key === DEFAULT_ESTIMATE_KEY)?.value?.trim();
  return value || DEFAULT_ESTIMATE_DOC_TYPE;
}

export function parseDocumentOrder(configs: { key: string; value?: string | null }[]): string[] {
  const config = configs.find(({ key }) => key === DOCUMENT_ORDER_KEY);
  if (!config?.value) return [];
  let order: unknown;
  try {
    order = JSON.parse(config.value);
  } catch {
    order = undefined;
  }
  if (!Array.isArray(order)) {
    console.warn(`Unable to parse ${DOCUMENT_ORDER_KEY}`);
    return [];
  }
  return order.filter((docType): docType is string => typeof docType === 'string');
}

export function orderDocumentSlots(slots: DocumentSlot[], order: string[]): DocumentSlot[] {
  const rank = (slot: DocumentSlot) => {
    const index = order.indexOf(slot.docType);
    return index === -1 ? order.length : index;
  };
  return [ ...slots ].sort((a, b) => rank(a) - rank(b));
}

export function parseDocumentSlots(configs: { key: string; value?: string | null }[]): DocumentSlot[] {
  const slots: DocumentSlot[] = [];
  for (const config of configs) {
    if (!config.key.startsWith(STANDARD_DOCUMENTS_PREFIX)) continue;
    let value: unknown;
    try {
      value = JSON.parse(config.value ?? '');
    } catch {
      console.warn(`Unable to parse ${config.key}`);
      continue;
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const { name, engine, templateUrl } = value as { name?: unknown; engine?: unknown; templateUrl?: unknown };
    const docType = config.key.slice(STANDARD_DOCUMENTS_PREFIX.length);
    slots.push({
      docType,
      name: typeof name === 'string' && name.trim() ? name : docType,
      ...(typeof engine === 'string' ? { engine } : {}),
      ...(typeof templateUrl === 'string' ? { templateUrl } : {}),
    });
  }
  return slots;
}

// Requirement: a document the user cannot generate must not be offered. Only
// Aegis slots with a template can be created; invoices are created from the
// invoice flow instead.
export const isCreatableDocumentSlot = (slot: DocumentSlot) =>
  slot.engine === 'aegis' && Boolean(slot.templateUrl?.trim()) && slot.docType !== 'invoice';

export const DocumentTypesActions = createActionGroup({
  source: ActionSource.DOCUMENT_TYPES,
  events: {
    'Load Success': props<{ zoneId: string; slots: DocumentSlot[]; defaultEstimate: string }>(),
    'Load Error': props<{ zoneId: string; error: Error }>(),
  },
});

interface DocumentTypesState {
  zoneId: string | undefined;
  slots: DocumentSlot[];
  defaultEstimate: string;
  loaded: boolean;
}

const documentTypesInitialState: DocumentTypesState = {
  zoneId: undefined,
  slots: [],
  defaultEstimate: DEFAULT_ESTIMATE_DOC_TYPE,
  loaded: false,
};

export const documentTypesFeature = createFeature({
  name: 'documentTypes',
  reducer: createReducer(
    documentTypesInitialState,
    on(DocumentTypesActions.loadSuccess, (_state, { zoneId, slots, defaultEstimate }): DocumentTypesState => ({
      zoneId,
      slots,
      defaultEstimate,
      loaded: true,
    })),
    on(DocumentTypesActions.loadError, (_state, { zoneId }): DocumentTypesState => ({
      ...documentTypesInitialState,
      zoneId,
      loaded: true,
    })),
  ),
});

export const selectCreatableDocumentTypes = createSelector(
  documentTypesFeature.selectSlots,
  (slots) => slots.filter(isCreatableDocumentSlot),
);

export const selectDocTypeNames = createSelector(
  documentTypesFeature.selectSlots,
  (slots) => slots.reduce<Record<string, string>>((names, slot) => {
    names[slot.docType] = slot.name;
    return names;
  }, {}),
);
